import * as tslib_1 from "tslib";
import { ShaderChunkBase } from "./ShaderChunkBase";
/**
 * SpecularBasicChunk provides the default shading method for Blinn-Phong specular highlights (an optimized but approximated
 * version of Phong specularity).
 */
var SpecularBasicChunk = (function (_super) {
    tslib_1.__extends(SpecularBasicChunk, _super);
    /**
     * Creates a new EffectEnvMapChunk.
     */
    function SpecularBasicChunk(method, shader) {
        var _this = _super.call(this, method, shader) || this;
        _this._method = method;
        _this._shader = shader;
        return _this;
    }
    SpecularBasicChunk.prototype._isUsed = function () {
        if (!this._shader.numLights)
            return false;
        return true;
    };
    /**
     * @inheritDoc
     */
    SpecularBasicChunk.prototype._initVO = function (chunkVO) {
        chunkVO.needsNormals = this._shader.numLights > 0;
        chunkVO.needsView = this._shader.numLights > 0;
        if (this._method.texture) {
            this._texture = this._shader.getAbstraction(this._method.texture);
            this._shader.uvDependencies++;
        }
        else if (this._texture) {
            this._texture = null;
        }
    };
    /**
     * @inheritDoc
     */
    SpecularBasicChunk.prototype._cleanCompilationData = function () {
        _super.prototype._cleanCompilationData.call(this);
        this._totalLightColorReg = null;
        this._specularTexData = null;
        this._specularDataRegister = null;
    };
    /**
     * @inheritDoc
     */
    SpecularBasicChunk.prototype._getFragmentPreLightingCode = function (registerCache, sharedRegisters) {
        var code = "";
        this._pIsFirstLight = true;
        this._specularDataRegister = registerCache.getFreeFragmentConstant();
        this._specularDataIndex = this._specularDataRegister.index * 4;
        if (this._texture) {
            this._specularTexData = registerCache.getFreeFragmentVectorTemp();
            registerCache.addFragmentTempUsages(this._specularTexData, 1);
            code += this._texture._getFragmentCode(this._specularTexData, registerCache, sharedRegisters, sharedRegisters.uvVarying);
        }
        this._totalLightColorReg = registerCache.getFreeFragmentVectorTemp();
        registerCache.addFragmentTempUsages(this._totalLightColorReg, 1);
        return code;
    };
    /**
     * @inheritDoc
     */
    SpecularBasicChunk.prototype._getFragmentCodePerLight = function (lightDirReg, lightColReg, registerCache, sharedRegisters) {
        var code = "";
        var t;
        if (this._pIsFirstLight) {
            t = this._totalLightColorReg;
        }
        else {
            t = registerCache.getFreeFragmentVectorTemp();
            registerCache.addFragmentTempUsages(t, 1);
        }
        var viewDirReg = sharedRegisters.viewDirFragment;
        var normalReg = sharedRegisters.normalFragment;
        // blinn-phong half vector model
        code += "add " + t + ", " + lightDirReg + ", " + viewDirReg + "\n" +
            "nrm " + t + ".xyz, " + t + "\n" +
            "dp3 " + t + ".w, " + normalReg + ", " + t + "\n" +
            "sat " + t + ".w, " + t + ".w\n";
        if (this._texture) {
            // apply gloss modulation from texture
            code += "mul " + this._specularTexData + ".w, " + this._specularTexData + ".y, " + this._specularDataRegister + ".w\n" +
                "pow " + t + ".w, " + t + ".w, " + this._specularTexData + ".w\n";
        }
        else {
            code += "pow " + t + ".w, " + t + ".w, " + this._specularDataRegister + ".w\n";
        }
        // attenuate
        if (this._shader.usesLightFallOff)
            code += "mul " + t + ".w, " + t + ".w, " + lightDirReg + ".w\n";
        if (this._modulateFunction != null)
            code += this._modulateFunction(t, registerCache, sharedRegisters);
        code += "mul " + t + ".xyz, " + lightColReg + ", " + t + ".w\n";
        if (!this._pIsFirstLight) {
            code += "add " + this._totalLightColorReg + ".xyz, " + this._totalLightColorReg + ", " + t + "\n";
            registerCache.removeFragmentTempUsage(t);
        }
        this._pIsFirstLight = false;
        return code;
    };
    /**
     * @inheritDoc
     */
    SpecularBasicChunk.prototype._getFragmentCodePerProbe = function (cubeMapReg, weightRegister, registerCache, sharedRegisters) {
        var code = "";
        var t;
        // write in temporary if not first light, so we can add to total diffuse colour
        if (this._pIsFirstLight) {
            t = this._totalLightColorReg;
        }
        else {
            t = registerCache.getFreeFragmentVectorTemp();
            registerCache.addFragmentTempUsages(t, 1);
        }
        var normalReg = sharedRegisters.normalFragment;
        var viewDirReg = sharedRegisters.viewDirFragment;
        code += "dp3 " + t + ".w, " + normalReg + ", " + viewDirReg + "\n" +
            "add " + t + ".w, " + t + ".w, " + t + ".w\n" +
            "mul " + t + ", " + t + ".w, " + normalReg + "\n" +
            "sub " + t + ", " + t + ", " + viewDirReg + "\n" +
            "tex " + t + ", " + t + ", " + cubeMapReg + " <cube," + "linear" + ",miplinear>\n" +
            "mul " + t + ".xyz, " + t + ", " + weightRegister + "\n";
        if (this._modulateFunction != null)
            code += this._modulateFunction(t, registerCache, sharedRegisters);
        if (!this._pIsFirstLight) {
            code += "add " + this._totalLightColorReg + ".xyz, " + this._totalLightColorReg + ", " + t + "\n";
            registerCache.removeFragmentTempUsage(t);
        }
        this._pIsFirstLight = false;
        return code;
    };
    /**
     * @inheritDoc
     */
    SpecularBasicChunk.prototype._getFragmentCode = function (targetReg, registerCache, sharedRegisters) {
        var code = "";
        if (sharedRegisters.shadowTarget)
            code += "mul " + this._totalLightColorReg + ".xyz, " + this._totalLightColorReg + ", " + sharedRegisters.shadowTarget + ".w\n";
        if (this._texture) {
            // apply strength modulation from texture
            code += "mul " + this._totalLightColorReg + ".xyz, " + this._totalLightColorReg + ", " + this._specularTexData + ".x\n";
            registerCache.removeFragmentTempUsage(this._specularTexData);
        }
        // apply material's specular reflection
        code += "mul " + this._totalLightColorReg + ".xyz, " + this._totalLightColorReg + ", " + this._specularDataRegister + "\n" +
            "add " + targetReg + ".xyz, " + targetReg + ", " + this._totalLightColorReg + "\n";
        registerCache.removeFragmentTempUsage(this._totalLightColorReg);
        return code;
    };
    /**
     * @inheritDoc
     */
    SpecularBasicChunk.prototype._activate = function () {
        if (this._texture)
            this._texture.activate();
        if (this._invalid) {
            var index = this._specularDataIndex;
            var data = this._shader.fragmentConstantData;
            data[index] = ((this._method.color >> 16) & 0xff) / 0xff * this._method.strength;
            data[index + 1] = ((this._method.color >> 8) & 0xff) / 0xff * this._method.strength;
            data[index + 2] = (this._method.color & 0xff) / 0xff * this._method.strength;
            data[index + 3] = this._method.gloss;
        }
    };
    SpecularBasicChunk.prototype.iSetRenderState = function (renderable, projection) {
        if (this._texture)
            this._texture._setRenderState(renderable);
    };
    return SpecularBasicChunk;
}(ShaderChunkBase));
export { SpecularBasicChunk };



// WEBPACK FOOTER //
// ../dist/lib/chunks/SpecularBasicChunk.js